import React, { useEffect } from "react";
import { useNavigate, Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SectionHeader, Reveal, Ornament } from "./Ornament";
import { JOURNAL_POSTS, FAQS, PRESS } from "../data/pages";
import { PORTFOLIO } from "../data/content";
import Seo from "./Seo";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "./ui/accordion";

const useTopScroll = () => {
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
};

const BackBar = () => {
  const nav = useNavigate();
  return (
    <div className="container-lux flex items-center justify-between pt-32">
      <button
        onClick={() => nav("/")}
        className="inline-flex items-center gap-2 font-micro text-[10px] text-[var(--lu-gold-dark)] hover:text-[var(--lu-gold)]"
      >
        <ArrowLeft size={14} />Back to Home</button>
      <button
        onClick={() => nav("/contact")}
        className="btn-gold-outline text-[10px] px-6 py-2"
      >
        Book
      </button>
    </div>
  );
};

/* ---------------- JOURNAL POST DETAIL ---------------- */
export function JournalPostPage() {
  useTopScroll();
  const { slug } = useParams();
  const post = JOURNAL_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/journal" replace />;
  const path = `/journal/${slug}`;
  const BASE = "https://lumiereevents.org";
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    image: [BASE + post.image],
    datePublished: post.date,
    author: { "@type": "Person", "name": "Stamatina Kanta" },
    publisher: { "@id": BASE + "/#org" },
    mainEntityOfPage: BASE + path,
    description: post.excerpt,
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: BASE + "/" },
      { "@type": "ListItem", position: 2, name: "Journal", item: BASE + "/journal" },
      { "@type": "ListItem", position: 3, name: post.title, item: BASE + path },
    ],
  };
  return (
    <div data-testid="journal-post-page">
      <Seo
        path={path}
        title={`${post.title} — Journal | Lumière Entertainment`}
        description={post.excerpt}
        image={post.image}
        jsonLd={[articleLd, breadcrumbLd]}
      />
      <BackBar />
      <article className="section-y">
        <div className="container-lux max-w-4xl">
          <p className="kicker text-[var(--lu-gold-dark)] text-center">{post.date}</p>
          <h1 className="mt-6 h-editorial text-[46px] sm:text-[60px] lg:text-[78px] text-center text-[var(--lu-black)]">
            {post.title}
          </h1>
          <Ornament className="mt-10" />
          <div className="mt-14 aspect-[16/9] tile">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>
          <div className="mt-14 space-y-6 text-[16px] leading-[1.95] text-black-80 font-light">
            <p className="font-serif italic text-[22px] text-[var(--lu-black)]">
              {post.excerpt}
            </p>
            <p>
              A Lumière evening begins long before the first note plays. It begins in the fitting room,
              in the way a fabric catches light at 6pm versus 9pm; in the whispered conversation between
              the maître d&apos; and the pastry chef about how to time the first course.
            </p>
            <p>
              We spend weeks visiting the venue at every hour of day, testing the acoustics of a
              particular corner, studying the way guests will move from the entrance to the terrace.
              Every performance element — the aerial silk drop, the fire flourish, the cue for the
              string quartet — is choreographed against these small, personal observations.
            </p>
            <p>
              The result, when it works, is invisible. Guests do not notice the structure. They only
              notice that they cannot remember a night quite like it. That is our craft.
            </p>
            <p className="font-serif italic text-[20px] text-[var(--lu-gold-dark)] pt-6">
              — Stamatina
            </p>
          </div>
        </div>
      </article>
    </div>
  );
}

/* ---------------- FULL PORTFOLIO ---------------- */
export function PortfolioPage() {
  useTopScroll();
  return (
    <div data-testid="portfolio-page">
      <Seo
        path="/portfolio"
        title="Portfolio File — Signature Productions | Lumière Entertainment"
        description="A curated visual archive of the latest Lumière productions in Europe and the Mediterranean."
      />
      <BackBar />
      <section className="section-y">
        <div className="container-lux">
          <SectionHeader
            kicker="The Complete File"
            title="Each"
            italicTitle="story we told."
            sub="A curated collection of producers from private villas, luxury weddings, hotel openings and brand nights in Europe and the Mediterranean."
          />
          <div className="mt-20 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {[...PORTFOLIO, ...PORTFOLIO].map((p, i) => (
              <Reveal key={i} delay={Math.min((i % 6) + 1, 6)}>
                <div className="tile aspect-[4/5]">
                  <img src={p.src} alt={p.label} className="w-full h-full object-cover" />
                  <div className="tile-cover-overlay" />
                  <div className="tile-caption">
                    <p className="kicker">Signature</p>
                    <p className="font-serif text-[20px] text-[var(--lu-ivory)] mt-1">{p.label}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------------- ABOUT PAGE ---------------- */
export function AboutPage() {
  useTopScroll();
  return (
    <div data-testid="about-page">
      <Seo
        path="/about"
        title="Stamatina Kanda — Founder Bio | Lumière Entertainment"
        description="Meet Stamatina Kanda, founder and creative director of Lumière Entertainment. Ten years in international show production before founding Lumière in 2016."
      />
      <BackBar />
      <section className="section-y">
        <div className="container-lux grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] tile">
              <img
                src="/generated/golden-performer-closeup.jpg"
                alt="Stamatina Kanda"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="kicker text-[var(--lu-gold-dark)]">Founder & Creative Director</p>
            <h1 className="mt-4 h-editorial text-[46px] sm:text-[60px] lg:text-[78px] text-[var(--lu-black)]">Stamatina<span className="italic text-[var(--lu-gold-dark)]">Sing</span>
            </h1>
            <Ornament className="my-8 justify-start" />
            <div className="space-y-5 text-[16px] leading-[1.9] text-black-80 font-light">
              <p className="font-serif italic text-[22px] text-[var(--lu-black)]">Every night I produce should feel like the first scene of a movie I'd like to see a hundred times.</p>
              <p>Born and raised between Greece and Italy, I spent a decade producing shows internationally — from Rio Carnival choreographies to residencies in five-star Dubai hotels — before founding Lumière Entertainment in 2016.</p>
              <p>What interests me the most: the small moments. The pause before the doors open. The way the candlelight catches the golden thread. The half second a guest realizes the evening will exceed their expectations.</p>
              <p>Today, Lumière produces bespoke entertainment in 12 countries, with a private roster of world-class performers, choreographers, costume designers and technicians — all sharing the same principle: nothing generic, nothing rushed, nothing flashy for no reason.</p>
              <p>If you're planning a night you want to be talked about for years — I'd love to help you build it.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------------- JOURNAL PAGE ---------------- */
export function JournalPage() {
  useTopScroll();
  return (
    <div data-testid="journal-page">
      <Seo
        path="/journal"
        title="Journal — Notes from the Atelier | Lumière Entertainment"
        description="Behind-the-scenes essays on luxury event production, styling, choreography and the craft of designing unforgettable nights."
      />
      <BackBar />
      <section className="section-y">
        <div className="container-lux">
          <SectionHeader
            kicker="Journal"
            title="Notes from"
            italicTitle="the atelier."
          />
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
            {JOURNAL_POSTS.map((p, i) => (
              <Reveal key={p.slug} delay={Math.min(i + 1, 6)}>
                <Link to={`/journal/${p.slug}`} className="block group" data-testid={`post-${p.slug}`}>
                  <div className="tile aspect-[4/5]">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                    <div className="tile-cover-overlay" />
                  </div>
                  <p className="mt-6 kicker text-[var(--lu-gold-dark)]">{p.date}</p>
                  <h3 className="mt-3 font-serif text-[26px] text-[var(--lu-black)] group-hover:text-[var(--lu-gold-dark)] transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[14px] leading-[1.8] text-black-70 font-light">{p.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------------- FAQ PAGE ---------------- */
export function FaqPage() {
  useTopScroll();
  return (
    <div data-testid="faq-page">
      <BackBar />
      <section className="section-y">
        <div className="container-lux max-w-4xl">
          <SectionHeader
            kicker="Frequently Asked"
            title="Answers, before"
            italicTitle="you even ask."
          />
          <Accordion type="single" collapsible className="mt-16" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[var(--lu-gold-dark)]/30">
                <AccordionTrigger className="text-left font-serif text-[22px] text-[var(--lu-black)] py-6 hover:no-underline hover:text-[var(--lu-gold-dark)]" data-testid={`faq-q-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[15px] leading-[1.9] text-black-70 pb-6 font-light">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}

/* ---------------- PRESS PAGE ---------------- */
export function PressPage() {
  useTopScroll();
  return (
    <div data-testid="press-page">
      <BackBar />
      <section className="section-y">
        <div className="container-lux">
          <SectionHeader
            kicker="In the Press"
            title="What the world"
            italicTitle="has been saying."
          />
          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-8">
            {PRESS.map((p, i) => (
              <Reveal key={p.outlet} delay={Math.min(i + 1, 6)}>
                <div className="border border-[var(--lu-gold-dark)]/40 p-10 bg-[var(--lu-cream)]/40" data-testid={`press-${i}`}>
                  <p className="kicker text-[var(--lu-gold-dark)]">{p.outlet}</p>
                  <p className="mt-6 font-serif italic text-[22px] leading-[1.5] text-[var(--lu-black)]">
                    &ldquo;{p.quote}&rdquo;
                  </p>
                  <p className="mt-6 font-micro text-[10px] text-black-60">{p.date}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
